import type { Instant } from '@js-joda/core';
import { createInstance } from 'src/api';
import type { schemas } from 'src/features/quant/schema';
import { appConfig } from 'src/lib/config';
import { container } from 'src/StaticContainer';
import type { z } from 'zod';

const quantApi = createInstance({ baseURL: appConfig.quantApi });

export const log = container.get('Logger').getSubLogger({ name: 'FilingsApi' });

type FilingsResponse = z.infer<typeof schemas.FilingsQueryResponse>;
type FilingItem = NonNullable<FilingsResponse['response']>[0];

type FilingTypesResponse = z.infer<typeof schemas.FilingTypesResponse>;
type FilingTagsResponse = z.infer<typeof schemas.FilingTagsResponse>;

export interface Filing {
    filingId: string;
    submittedTimestamp: Instant;
    uploadedTimestamp: Instant;
    symbol: string;
    type: string;
    filingName: string;
    eventDetails: string | null;
    link: string;
    isSummarizable: boolean;
}

export async function getFilings({
    symbols,
    filingTypes = [],
    searchString,
    tags8K,
    start,
    end,
    submittedTimestamp,
    filingId,
    direction = 'find_older',
    limit,
    sortBy = 'submittedTimestamp',
    abortSignal,
}: {
    symbols: string[];
    filingTypes: string[];
    searchString?: string;
    tags8K?: string[];
    start?: string;
    end?: string;
    submittedTimestamp?: string;
    filingId?: string;
    direction?: 'find_older' | 'find_newer';
    limit?: number;
    sortBy?: 'submittedTimestamp' | 'uploadedTimestamp';
    abortSignal?: AbortSignal;
}): Promise<Filing[]> {
    // If symbols is empty, return an empty array
    if (symbols.length === 0) {
        return [];
    }

    const res = await quantApi.post<FilingsResponse>(
        `/v1/filings/query`,
        {
            symbols,
            filingTypes,
            searchString,
            tags8K: tags8K as any, // TODO: Type tags8K properly with the specific literal union from schema
            start,
            end,
            limit,
            sortBy,
            pagination: submittedTimestamp
                ? {
                      submittedTimestamp,
                      filingId,
                      direction,
                  }
                : undefined,
        },
        { signal: abortSignal },
    );

    try {
        const filings = res.data.response;
        if (!filings) {
            return [];
        }

        return filings.map((filing: FilingItem): Filing => {
            return {
                filingId: filing.FilingId,
                // dates are strings from the API, but we parse them to an instant
                // via the reviver function
                submittedTimestamp: filing.SubmittedTimestamp as unknown as Instant,
                uploadedTimestamp: filing.UploadedTimestamp as unknown as Instant,
                symbol: filing.Symbol,
                type: filing.Type,
                filingName: filing.FilingName,
                eventDetails: filing.EventDetails,
                link: filing.Link,
                isSummarizable: filing.IsSummarizable,
            };
        });
    } catch (e) {
        log.error('Failed to map filings', { error: e });
        throw e;
    }
}

export async function getFilingTypes(): Promise<string[]> {
    try {
        const res = await quantApi.get<FilingTypesResponse>('/v1/filings/types');
        return res.data.response ?? [];
    } catch (e) {
        log.error('Failed to fetch filing types', { error: e });
        throw e;
    }
}

export async function getFilingTags(): Promise<string[]> {
    try {
        const res = await quantApi.get<FilingTagsResponse>('/v1/filings/tags');
        return res.data.response ?? [];
    } catch (e) {
        log.error('Failed to fetch filing tags', { error: e });
        throw e;
    }
}

type FilingSummaryResponse = z.infer<typeof schemas.FilingsSummaryResponse>;

export async function getFilingSummary(filingId: string): Promise<string> {
    try {
        const res = await quantApi.get<FilingSummaryResponse>(`/v1/filings/${filingId}/summary`);
        return res.data.response ?? '';
    } catch (e) {
        log.error('Failed to fetch filing summary', { error: e, filingId });
        throw e;
    }
}
